<template>
  <!--
    The topbar field IS the search input. The results panel opens directly under it at the same
    width (it is not portaled, so the listbox keeps arrow-key navigation between the input and
    the results). The field widens while active so longer queries fit.
  -->
  <CommandStatic
    ref="rootRef"
    class="relative h-auto w-auto overflow-visible rounded-none bg-transparent text-inherit"
  >
    <div
      class="flex h-9 items-center gap-2 rounded-lg border px-3 transition-[width,background-color,border-color] duration-200"
      :class="
        searchMenu
          ? 'w-80 border-white/40 bg-white/15'
          : 'w-56 cursor-text border-white/15 bg-white/5 hover:border-white/30 hover:bg-white/10'
      "
      @click="openSearch"
    >
      <Search class="size-4 shrink-0 text-white/70" />
      <ListboxFilter
        ref="inputRef"
        v-model="searchText"
        :placeholder="t('search.label')"
        class="h-full min-w-0 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-white/70"
        @focus="searchMenu = true"
        @keydown.esc.prevent="closeSearch"
      />

      <KbdGroup v-if="!searchMenu && !searchText" class="ml-auto">
        <template v-if="getOS === 'apple'">
          <Kbd class="bg-white/10 text-white/70">⌘</Kbd>
          <Kbd class="bg-white/10 text-white/70">P</Kbd>
        </template>
        <template v-else>
          <Kbd class="bg-white/10 text-white/70">ctrl</Kbd>
          <Kbd class="bg-white/10 text-white/70">P</Kbd>
        </template>
      </KbdGroup>
      <Kbd v-else-if="searchMenu" class="ml-auto bg-white/10 text-white/70">esc</Kbd>
    </div>

    <div
      v-if="searchMenu"
      class="absolute right-0 top-full z-50 mt-1.5 w-full overflow-hidden rounded-lg border bg-popover text-popover-foreground shadow-lg"
    >
          <div v-if="noResults" class="py-6 text-center text-sm">
            {{ t("search.empty") }}
          </div>

          <CommandList v-if="searchText">
            <CommandGroup :heading="t('search.topics')">
              <CommandItem
                v-for="item in intentResults"
                :key="item.type + '-' + item.value.intent"
                :value="item.displayName"
                class="cursor-pointer"
                @select="callItem(item)"
              >
                <component :is="iconFor(item.icon)" class="mr-2 size-4 shrink-0 text-muted-foreground" />
                {{
                  displayName(item.value.intent, locale) ||
                  item.displayName
                }}
              </CommandItem>
            </CommandGroup>

            <CommandGroup v-if="isMultiBot || isAdmin" heading="Bots">
              <CommandItem
                v-for="item in botResults"
                :key="item.type + '-' + item.value.intent"
                :value="item.displayName"
                class="cursor-pointer"
                @select="callItem(item)"
              >
                {{
                  displayName(item.value.intent, locale) ||
                  item.displayName
                }}
              </CommandItem>
            </CommandGroup>
          </CommandList>

          <CommandList v-else-if="queries.length">
            <CommandGroup :heading="t('search.history')">
              <CommandItem
                v-for="(query, index) in queries"
                :key="index"
                :value="query.displayName"
                class="cursor-pointer justify-between"
                @select="callItem(query)"
              >
                <span>
                  {{ query.displayName }}
                </span>

                <Button
                  class="cursor-pointer"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Remove item"
                  @keydown.enter.stop="removeItem(query)"
                  @click.stop="removeItem(query)"
                >
                  <X />
                </Button>
              </CommandItem>
            </CommandGroup>
          </CommandList>

          <div v-else class="py-6 text-center text-sm">
            {{ t("search.description") }}
          </div>
    </div>
  </CommandStatic>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { useSearchStore } from "@/stores/search";
import { useAuthStore } from "@/stores/auth";
import { useIntentsStore } from "@/stores/intents";
import { useBotsStore } from "@/stores/bots";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import _ from "lodash";
import OsUtil from "@/utils/os";
import { Kbd, KbdGroup } from "@/components/ui/kbd";
import { Button } from "@/components/ui/button";
import { ListboxFilter } from "reka-ui";
import { onClickOutside } from "@vueuse/core";
import {
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command/fuse";
import CommandStatic from "@/components/ui/command/fuse/CommandStatic.vue";
import { Bot, FileText, Search, Sparkles, X } from "@lucide/vue";

/**
 * Maps the semantic icon keys emitted by the search store
 * (src/store/modules/search.js) to Lucide components used in the result lists.
 */
const SEARCH_ICONS: Record<string, any> = {
  suggestion: Sparkles,
  intent: FileText,
  bot: Bot,
};

const searchStore = useSearchStore();
const authStore = useAuthStore();
const intentsStore = useIntentsStore();
const botsStore = useBotsStore();
const router = useRouter();
const route = useRoute();
const { t, locale } = useI18n();

const searchMenu = ref(false);
const searchText = ref("");
const lastQueries = ref<any[]>([]);
const searching = ref(false);

const botResults = computed(() => searchStore.botResultsGetter);
const queries = computed(() => searchStore.queries);
const intentResults = computed(() => searchStore.intentResultsGetter);
const isAdmin = computed(() => authStore.isAdmin);
const isMultiBot = computed(() => authStore.isMultiBot);
const displayName = computed(() => intentsStore.displayName);

const noResults = computed(() => {
  return (
    searchText.value &&
    botResults.value.length === 0 &&
    intentResults.value.length === 0 &&
    !searching.value
  );
});

const getOS = computed(() => OsUtil.getOs());

function onKeyEvent(event: KeyboardEvent) {
  if (getOS.value === "apple" && event.key === "p" && event.metaKey) {
    event.preventDefault();
    openSearch();
  } else if (event.key === "p" && event.ctrlKey) {
    event.preventDefault();
    openSearch();
  }
}

const rootRef = ref<any>(null);
const inputRef = ref<any>(null);

/** The rendered <input> behind the reka ListboxFilter component ref. */
function inputEl(): HTMLInputElement | null {
  const el = inputRef.value?.$el ?? inputRef.value;
  return el instanceof HTMLInputElement ? el : el?.querySelector?.("input") ?? null;
}

function openSearch() {
  searchMenu.value = true;
  nextTick(() => inputEl()?.focus());
}

function closeSearch() {
  searchMenu.value = false;
  inputEl()?.blur();
}

onClickOutside(rootRef, () => {
  if (searchMenu.value) searchMenu.value = false;
});

/**
 * Resolves the Lucide icon component for a search result's icon name.
 */
function iconFor(icon: string) {
  return SEARCH_ICONS[icon] || FileText;
}

async function updateSearch(text?: string) {
  searching.value = true;
  await searchStore.search({ text: text?.trim() });
  searching.value = false;
}

function removeItem(item: any) {
  searchStore.removeQuery(item);
}

function callItem(item: any) {
  if (isAdmin.value && item.value?.suggested) {
    router.push({
      path: `/intent/${item.value.intent}/training`,
    });
    return;
  }

  if (item.type === "bot") {
    botsStore.changeBot({
      uniqueBotId: item.value.uniqueBotId,
      router,
    });
  }

  if (item.type === "intent") {
    if (
      route.name !== "intentsedit" ||
      route.params.name !== item.value.intent
    ) {
      router.push({
        name: "intentsedit",
        params: { name: item.value.intent },
      });
    }
  }

  closeSearch();
  searchText.value = "";
  searchStore.pick(item);
}

watch(searchText, (newText) => {
  updateSearch(newText);
});

watch(searchMenu, (opened) => {
  if (opened) {
    lastQueries.value = _.cloneDeep(queries.value);
    // Reopening with a query keeps its results; highlight the first so Enter works right away.
    nextTick(() => rootRef.value?.highlightFirstItem?.());
  }
});

document.addEventListener("keydown", onKeyEvent);

// Start indexing bots and intents in the background by just calling the getters
setTimeout(() => {
  searchStore.botIndex;
  searchStore.intentIndex;
}, 0);

onBeforeUnmount(() => {
  document.removeEventListener("keydown", onKeyEvent);
});
</script>
